import { useCallback, useState } from "react";

export type GeoCoords = { lat: number; lon: number };

function friendlyGeoError(err: GeolocationPositionError): string {
  switch (err.code) {
    case GeolocationPositionError.PERMISSION_DENIED:
      // Covers iOS kCLErrorDomain error 0 (denied) and browser permission denied
      return "Location access denied. Go to Settings → Privacy → Location Services and enable it for your browser or Barrelcast.";
    case GeolocationPositionError.POSITION_UNAVAILABLE:
      // Covers iOS kCLErrorDomain error 1 (unavailable)
      return "Your location is currently unavailable. Try moving to an area with better signal.";
    case GeolocationPositionError.TIMEOUT:
      // Covers iOS kCLErrorDomain error 2 (timeout)
      return "Location request timed out. Please try again.";
    default:
      return "Couldn't get your location. Please try again.";
  }
}

export function useGeolocation() {
  const [coords, setCoords] = useState<GeoCoords | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setError("Geolocation is not supported on this device.");
      return;
    }
    setLoading(true);
    setError(null);
    setCoords(null); // Clear previous coords to force fresh lookup
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newCoords = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setCoords(newCoords);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(friendlyGeoError(err));
        setLoading(false);
        setCoords(null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 } // maximumAge: 0 forces fresh lookup every time
    );
  }, []);

  return { coords, loading, error, request };
}
